import React, { useState, useEffect } from 'react'
import ReactModal from 'react-modal'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Button,
  IconButton,
  Chip
} from '@mui/material'
import { Edit, Trash2, Eye, Plus, Share2 } from 'lucide-react'
import { assessmentsApi, questionsApi } from '../../api/assessments'
import type { Assessment, QuestionResponse } from '../../types/assessments'
import { AssessmentPreviewModal } from '../../components/assessments/AssessmentPreviewModal'
import { EditAssessmentModal } from '../../components/assessments/EditAssessmentModal'
import axios from 'axios'
import { toast } from 'react-toastify'
import { usePlan } from '../../context/PlanContext'

const GetAssessment: React.FC = () => {
  const { currentPlan } = usePlan()
  const navigate = useNavigate()
  const [assessments, setAssessments] = useState<Assessment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState<any>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [assessmentToDelete, setAssessmentToDelete] = useState<any>(null)

  useEffect(() => {
    fetchAssessments()
  }, [])

  const fetchAssessments = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth-token')
      const apiUrl = process.env.REACT_APP_RESUME_PARSER_URL || 'http://localhost:4000'
      const response = await axios.get(
        `${apiUrl}/api/assessment/users/${currentPlan?.bgAccountId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
      console.log(response.data)
      setAssessments(response.data.data)
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load assessments')
    } finally {
      setLoading(false)
    }
  }

  const handlePreview = (assessment: Assessment) => {
    console.log(assessment, 'test')
    setSelectedAssessment(assessment)
    setPreviewModalOpen(true)
  }

  const handleEdit = (assessment: Assessment) => {
    setSelectedAssessment(assessment)
    setEditModalOpen(true)
  }

  const handleDelete = (assessment: Assessment) => {
    console.log(assessment)
    setAssessmentToDelete(assessment)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (!assessmentToDelete) return

    try {
      const token = localStorage.getItem('auth-token')
      const apiUrl = process.env.REACT_APP_RESUME_PARSER_URL || 'http://localhost:4000'
      const response = await axios.delete(`${apiUrl}/api/assessment/${assessmentToDelete.uuid}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      console.log(response.data)
      setAssessments((prev) => prev.filter((a) => a.uuid !== assessmentToDelete.uuid))
      setDeleteConfirmOpen(false)
      setAssessmentToDelete(null)
      toast.success('Assessment deleted successfully')
    } catch (err: any) {
      console.error('Failed to delete assessment:', err)
      toast.error('Failed to delete assessment')
    }
  }

  const handleClosePreview = () => {
    setPreviewModalOpen(false)
    setSelectedAssessment(null)
  }

  const handleCloseEdit = () => {
    setEditModalOpen(false)
    setSelectedAssessment(null)
    fetchAssessments() // Refresh list after edit
  }

  if (loading) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Alert severity='error'>{error}</Alert>
      </Container>
    )
  }

  return (
    <>
      <section className='row' style={{ padding: '16px 20px' }}>
        <div className='col-12'>
          <div className='card' style={{ border: 'none', boxShadow: 'none' }}>
            <div
              className='card-header'
              style={{ borderBottom: '1px solid #e0e0e0', padding: '20px 20px' }}
            >
              <div className='d-flex justify-content-between align-items-center w-100'>
                <div>
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.75rem',
                      mb: 0.5,
                      color: '#212121'
                    }}
                  >
                    All Assessments
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      color: '#757575',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    Manage and preview your assessments
                  </Typography>
                </div>
                <Button
                  variant='contained'
                  startIcon={<Plus size={18} />}
                  onClick={() => navigate('/assessments/templates')}
                  sx={{
                    backgroundColor: '#1976d2',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 3,
                    py: 1.25,
                    borderRadius: 2,
                    boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                      boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)'
                    }
                  }}
                >
                  + CREATE ASSESSMENT
                </Button>
              </div>
            </div>

            <div className='card-body' style={{ padding: '16px 20px' }}>
              {assessments.length === 0 ? (
                <Box
                  textAlign='center'
                  py={10}
                  sx={{
                    backgroundColor: '#fafafa',
                    borderRadius: 2,
                    border: '1px dashed #e0e0e0'
                  }}
                >
                  <Typography variant='h6' color='text.secondary' sx={{ mb: 1, fontWeight: 500 }}>
                    No assessments created yet
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                    Create your first assessment from templates
                  </Typography>
                  <Button
                    variant='contained'
                    startIcon={<Plus size={18} />}
                    onClick={() => navigate('/assessments/templates')}
                    sx={{
                      backgroundColor: '#1976d2',
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Create Assessment
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)',
                      lg: 'repeat(4, 1fr)'
                    },
                    gap: 2.5
                  }}
                >
                  {assessments?.map((assessment) => {
                    // Render icon similar to template card
                    const renderIcon = () => {
                      const isFontAwesome =
                        assessment.icon &&
                        typeof assessment.icon === 'string' &&
                        assessment.icon.startsWith('fa-')

                      if (isFontAwesome) {
                        // Check if it already has a prefix (fas, far, fab, fal)
                        const hasPrefix =
                          assessment.icon.startsWith('fas ') ||
                          assessment.icon.startsWith('far ') ||
                          assessment.icon.startsWith('fab ') ||
                          assessment.icon.startsWith('fal ')

                        // Use 'fas' (solid) as default prefix for FontAwesome 5/6 icons
                        const iconClass = hasPrefix ? assessment.icon : `fas ${assessment.icon}`

                        return (
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 2,
                              backgroundColor: assessment.color || '#3498db',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1.5,
                              boxShadow: `0 2px 8px ${assessment.color}40`
                            }}
                          >
                            <i
                              className={iconClass}
                              style={{ fontSize: '28px', color: '#fff' }}
                            ></i>
                          </Box>
                        )
                      }

                      // Handle emoji icons (legacy support)
                      if (assessment.icon === '🛡️') {
                        return (
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 2,
                              backgroundColor: assessment.color || '#e74c3c',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1.5,
                              border: '2px solid',
                              borderColor: assessment.color || '#e74c3c',
                              position: 'relative',
                              boxShadow: `0 2px 8px ${assessment.color}40`
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#fff',
                                fontSize: '32px',
                                fontWeight: 'bold',
                                fontFamily: 'Arial, sans-serif',
                                lineHeight: 1
                              }}
                            >
                              D
                            </Typography>
                          </Box>
                        )
                      }

                      // Default: render as emoji or text
                      return (
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            backgroundColor: assessment.color || '#3498db',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1.5,
                            fontSize: '36px',
                            boxShadow: `0 2px 8px ${assessment.color}40`
                          }}
                        >
                          {assessment.icon || '📝'}
                        </Box>
                      )
                    }

                    return (
                      <Paper
                        key={assessment.uuid}
                        sx={{
                          minHeight: '380px',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 3,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.3s ease',
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                          },
                          position: 'relative',
                          overflow: 'visible'
                        }}
                      >
                        {/* Share Button */}
                        <IconButton
                          size='small'
                          onClick={async (e) => {
                            e.stopPropagation()
                            try {
                              const shareableLink = assessment.shareableLink
                              if (shareableLink) {
                                await navigator.clipboard.writeText(shareableLink)
                                toast.success('Link copied to clipboard!')
                              } else {
                                toast.warning('No shareable link available for this assessment')
                              }
                            } catch (error) {
                              console.error('Failed to copy link:', error)
                              toast.error('Failed to copy link')
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(245, 245, 245, 0.9)',
                              color: assessment.color
                            },
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            width: 32,
                            height: 32
                          }}
                        >
                          <Share2 size={16} />
                        </IconButton>
                        <Box
                          sx={{
                            p: 2.5,
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: '380px'
                          }}
                        >
                          {/* Icon */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            {renderIcon()}
                          </Box>

                          {/* Title */}
                          <Typography
                            variant='h5'
                            sx={{
                              fontWeight: 700,
                              mb: 0.75,
                              textAlign: 'center',
                              color: '#212121',
                              fontSize: '1.1rem',
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {assessment.title}
                          </Typography>

                          {/* Subtitle */}
                          <Typography
                            variant='body2'
                            sx={{
                              mb: 1.5,
                              textAlign: 'center',
                              color: 'text.secondary',
                              fontSize: '0.813rem',
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {assessment.subtitle}
                          </Typography>

                          {/* Assessment Details */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: 3,
                              mb: 2,
                              alignItems: 'center'
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                              }}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 1,
                                  backgroundColor: `${assessment.color}20`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 0.5
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: assessment.color,
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  ✓
                                </Typography>
                              </Box>
                              <Typography
                                variant='caption'
                                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                              >
                                15 Questions
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                              }}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 1,
                                  backgroundColor: `${assessment.color}20`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 0.5
                                }}
                              >
                                <Typography sx={{ color: assessment.color, fontSize: '16px' }}>
                                  ⏱
                                </Typography>
                              </Box>
                              <Typography
                                variant='caption'
                                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                              >
                                5 min
                              </Typography>
                            </Box>
                          </Box>

                          {/* Description */}
                          <Typography
                            variant='body2'
                            sx={{
                              mb: 2,
                              textAlign: 'center',
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                              flexGrow: 1,
                              lineHeight: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {assessment.description}
                          </Typography>

                          {/* Actions Section */}
                          <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                            <Button
                              variant='contained'
                              fullWidth
                              startIcon={<Eye size={14} />}
                              onClick={() => handlePreview(assessment)}
                              sx={{
                                background: `linear-gradient(180deg, ${assessment.color} 0%, ${assessment.color}dd 100%)`,
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                py: 1.25,
                                borderRadius: 2,
                                boxShadow: `0 2px 4px ${assessment.color}40`,
                                '&:hover': {
                                  background: `linear-gradient(180deg, ${assessment.color}dd 0%, ${assessment.color} 100%)`,
                                  boxShadow: `0 4px 8px ${assessment.color}50`
                                }
                              }}
                            >
                              Preview
                            </Button>
                            <IconButton
                              size='small'
                              onClick={() => handleEdit(assessment)}
                              sx={{
                                border: '1px solid #e0e0e0',
                                color: '#424242',
                                minWidth: '48px',
                                '&:hover': {
                                  borderColor: assessment.color,
                                  backgroundColor: `${assessment.color}08`,
                                  color: assessment.color
                                }
                              }}
                            >
                              <Edit size={16} />
                            </IconButton>
                            <IconButton
                              size='small'
                              onClick={() => handleDelete(assessment)}
                              sx={{
                                border: '1px solid #e0e0e0',
                                color: '#d32f2f',
                                minWidth: '48px',
                                '&:hover': {
                                  borderColor: '#d32f2f',
                                  backgroundColor: '#ffebee',
                                  color: '#c62828'
                                }
                              }}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    )
                  })}
                </Box>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Preview Modal */}
      {selectedAssessment && (
        <AssessmentPreviewModal
          isOpen={previewModalOpen}
          onClose={handleClosePreview}
          assessment={selectedAssessment}
        />
      )}

      {/* Edit Modal */}
      {selectedAssessment && (
        <EditAssessmentModal
          isOpen={editModalOpen}
          onClose={handleCloseEdit}
          assessment={selectedAssessment}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ReactModal
        isOpen={deleteConfirmOpen}
        onRequestClose={() => {
          setDeleteConfirmOpen(false)
          setAssessmentToDelete(null)
        }}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '15px',
            border: 'none',
            boxShadow: '0 2px 8px 0 #00000029',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
          }
        }}
        contentLabel='Delete Assessment'
      >
        <div className='card'>
          <div className='card-header'>
            <h3 className='card-title fw-bolder'>Confirm Delete</h3>
          </div>
          <div className='card-body'>
            <p>
              Are you sure you want to delete <strong>{assessmentToDelete?.title}</strong>? This
              action cannot be undone.
            </p>
          </div>
          <div className='card-footer d-flex justify-content-end gap-2'>
            <button
              type='button'
              className='btn btn-light'
              onClick={() => {
                setDeleteConfirmOpen(false)
                setAssessmentToDelete(null)
              }}
            >
              Cancel
            </button>
            <button type='button' className='btn btn-danger' onClick={confirmDelete}>
              Delete
            </button>
          </div>
        </div>
      </ReactModal>
    </>
  )
}

export default GetAssessment
